/**
 * The Sun — WinterMute's body during the Rite, and the Watcher's eye afterwards.
 *
 * Renders `eye.json` as a large centred point cloud with ADDITIVE blending. That
 * one choice does most of the work: a black point contributes nothing to the
 * framebuffer, so the dark areas of the mandala are transparent for free. No
 * alpha mask, no cutout, and the camera shows straight through once it fades up
 * underneath. It is also why the Watcher overlay can sit on top of a live frame
 * without dimming it.
 *
 * ── Why the blink is procedural ────────────────────────────────────────────
 * Three blink frames were delivered as separate point clouds. They could not be
 * used: dart-throwing had terminated early, leaving ~40% of each image with no
 * points at all, and each frame was sampled independently so no point in frame 1
 * corresponds to any point in frame 2. `scripts/align-blink.mjs` fixes the
 * correspondence and can fill the gaps from `eye.json`, but it cannot invent the
 * missing mandala — the result is a visibly asymmetric void.
 *
 * So the eyelid is a shader uniform over the ONE good cloud instead. It costs no
 * extra download, has full coverage, and is tunable at runtime rather than baked.
 * If the three frames are ever re-exported with full coverage AND shared
 * positions, `u_blink` can be swapped for a colour lerp between buffers without
 * touching anything else.
 */

import type { IconCloud } from '../Iconostasis/loadIcon.js';
import { applyPs1Upscale, ps1Dpr } from '../postfx/ps1.js';

export interface SunFrame {
  /** Seconds since the sun appeared. Drives rotation, breath, glint. */
  timeSec: number;
  /** 0 hidden .. 1 fully present. */
  presence: number;
  /** 0 open .. 1 shut. The eyelid. */
  blink: number;
  /** Screen centre in CSS px. */
  cx: number;
  cy: number;
  /** Diameter in CSS px. */
  size: number;
  /** Overall brightness multiplier, on top of presence. */
  gain: number;
  /** PS1 vertex snap grid in device px. 0 disables. */
  snap: number;
}

const VERT = `
precision mediump float;

attribute vec2 a_pos;   // icon space 0..1, y down
attribute vec3 a_col;
attribute float a_seed;

uniform vec2  u_centre;
uniform vec2  u_halfSize;
uniform float u_time;
uniform float u_presence;
uniform float u_blink;
uniform float u_pointSize;
uniform float u_gain;
uniform vec2  u_snap;     // clip-space quantum, 0 = off

varying vec3 v_col;
varying float v_alpha;

void main() {
  float r1 = fract(sin(a_seed * 127.1) * 43758.5453);
  float r2 = fract(sin(a_seed * 311.7) * 24634.6345);

  // Centred, y flipped.
  vec2 p = vec2(a_pos.x - 0.5, 0.5 - a_pos.y) * 2.0;
  float radius = length(p);
  float ang = atan(p.y, p.x);

  // Differential rotation. Speed falls off with radius so the rings shear past
  // one another like an accretion disc instead of turning as one rigid wheel.
  // This is the halo movement — a rigid spin reads as a loading spinner.
  float spin = u_time * (0.16 / (0.28 + radius * 1.7));
  ang += spin;

  // Radial breath, a few percent, slow.
  float breath = 1.0 + sin(u_time * 0.42 + radius * 5.0) * 0.018;
  float rr = radius * breath;

  // The eyelid. Two lids closing toward the horizontal midline, with a slight
  // curve so it reads as an eye rather than a shutter. Points inside the closing
  // band are pushed toward the midline and dimmed, so the mandala appears to
  // fold shut rather than simply fade.
  float lid = u_blink;
  vec2 q = vec2(cos(ang), sin(ang)) * rr;
  float curve = 1.0 - 0.35 * q.x * q.x;          // lids bow toward the corners
  float openHeight = mix(1.15, 0.0, lid) * curve;
  float overlap = abs(q.y) - openHeight;
  float shut = clamp(overlap / 0.35, 0.0, 1.0);
  q.y = mix(q.y, sign(q.y) * openHeight, shut * 0.85);

  // Presence: at 0 the points sit scattered far out and arrive as it rises.
  float stagger = clamp((u_presence - r2 * 0.5) / 0.5, 0.0, 1.0);
  float ease = stagger * stagger * (3.0 - 2.0 * stagger);
  vec2 scattered = q * (1.0 + (1.2 + r1 * 2.0));
  vec2 fp = mix(scattered, q, ease);

  vec2 clip = u_centre + fp * u_halfSize;

  // PS1 vertex snapping — the console had no sub-pixel precision, and the
  // resulting jitter is the single most recognisable part of the look.
  if (u_snap.x > 0.0) clip = floor(clip / u_snap + 0.5) * u_snap;

  gl_Position = vec4(clip, 0.0, 1.0);

  // Glint: a brief star flare on bright points, on a staggered per-point timer.
  float lumi = dot(a_col, vec3(0.2126, 0.7152, 0.0722));
  float phase = fract(u_time * 0.11 + r1);
  float flare = smoothstep(0.97, 1.0, phase) * step(0.45, lumi);

  gl_PointSize = max(1.0, u_pointSize * mix(0.5, 1.0, ease) * (1.0 + flare * 2.4));

  v_col = a_col * (1.0 + flare * 1.6);
  // Dimmed where the lid has closed over it, and faded while still arriving.
  v_alpha = u_presence * u_gain * ease * (1.0 - shut * 0.92);
}
`;

const FRAG = `
precision mediump float;
varying vec3 v_col;
varying float v_alpha;
void main() {
  vec2 d = gl_PointCoord - 0.5;
  float r = dot(d, d);
  if (r > 0.25) discard;
  float a = v_alpha * smoothstep(0.25, 0.06, r);
  if (a <= 0.003) discard;
  // Additive: colour scaled by coverage, zero alpha contribution. Black points
  // add nothing, which is what makes the dark ground translucent.
  gl_FragColor = vec4(v_col * a, 0.0);
}
`;

export class SunRenderer {
  private gl: WebGLRenderingContext | null = null;
  private prog: WebGLProgram | null = null;
  private loc: Record<string, WebGLUniformLocation | null> = {};
  private attr: Record<string, number> = {};
  private bufs: { pos: WebGLBuffer; col: WebGLBuffer; seed: WebGLBuffer; count: number } | null =
    null;
  private w = 0;
  private h = 0;
  private dpr = 1;

  constructor(private canvas: HTMLCanvasElement) {
    const gl =
      (canvas.getContext('webgl', {
        alpha: true,
        premultipliedAlpha: true,
        antialias: true,
      }) as WebGLRenderingContext | null) ??
      (canvas.getContext('experimental-webgl') as WebGLRenderingContext | null);
    if (!gl) return;
    const vs = compile(gl, gl.VERTEX_SHADER, VERT);
    const fs = compile(gl, gl.FRAGMENT_SHADER, FRAG);
    if (!vs || !fs) return;
    const prog = gl.createProgram();
    if (!prog) return;
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      console.warn('[Sun] link failed:', gl.getProgramInfoLog(prog));
      return;
    }
    this.gl = gl;
    this.prog = prog;
    for (const n of ['a_pos', 'a_col', 'a_seed']) this.attr[n] = gl.getAttribLocation(prog, n);
    for (const n of [
      'u_centre', 'u_halfSize', 'u_time', 'u_presence', 'u_blink',
      'u_pointSize', 'u_gain', 'u_snap',
    ]) {
      this.loc[n] = gl.getUniformLocation(prog, n);
    }
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE); // additive
  }

  get ready(): boolean {
    return this.gl !== null;
  }

  resize(cssW: number, cssH: number, dpr: number): void {
    const d = ps1Dpr(dpr);
    this.w = cssW;
    this.h = cssH;
    this.dpr = d;
    this.canvas.width = Math.max(1, Math.round(cssW * d));
    this.canvas.height = Math.max(1, Math.round(cssH * d));
    applyPs1Upscale(this.canvas);
    this.gl?.viewport(0, 0, this.canvas.width, this.canvas.height);
  }

  upload(cloud: IconCloud): void {
    const gl = this.gl;
    if (!gl || this.bufs) return;
    const pos = gl.createBuffer();
    const col = gl.createBuffer();
    const seed = gl.createBuffer();
    if (!pos || !col || !seed) return;
    gl.bindBuffer(gl.ARRAY_BUFFER, pos);
    gl.bufferData(gl.ARRAY_BUFFER, cloud.pos, gl.STATIC_DRAW);
    gl.bindBuffer(gl.ARRAY_BUFFER, col);
    gl.bufferData(gl.ARRAY_BUFFER, cloud.col, gl.STATIC_DRAW);
    const seeds = new Float32Array(cloud.count);
    for (let i = 0; i < cloud.count; i++) seeds[i] = i / cloud.count;
    gl.bindBuffer(gl.ARRAY_BUFFER, seed);
    gl.bufferData(gl.ARRAY_BUFFER, seeds, gl.STATIC_DRAW);
    this.bufs = { pos, col, seed, count: cloud.count };
  }

  draw(f: SunFrame): void {
    const gl = this.gl;
    const b = this.bufs;
    if (!gl || !b || !this.prog) return;

    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    if (f.presence <= 0.001) return;

    gl.useProgram(this.prog);
    gl.bindBuffer(gl.ARRAY_BUFFER, b.pos);
    gl.enableVertexAttribArray(this.attr.a_pos!);
    gl.vertexAttribPointer(this.attr.a_pos!, 2, gl.FLOAT, false, 0, 0);
    gl.bindBuffer(gl.ARRAY_BUFFER, b.col);
    gl.enableVertexAttribArray(this.attr.a_col!);
    gl.vertexAttribPointer(this.attr.a_col!, 3, gl.FLOAT, false, 0, 0);
    gl.bindBuffer(gl.ARRAY_BUFFER, b.seed);
    gl.enableVertexAttribArray(this.attr.a_seed!);
    gl.vertexAttribPointer(this.attr.a_seed!, 1, gl.FLOAT, false, 0, 0);

    const half = f.size / 2;
    gl.uniform2f(this.loc.u_centre!, (f.cx / this.w) * 2 - 1, 1 - (f.cy / this.h) * 2);
    gl.uniform2f(this.loc.u_halfSize!, half / this.w, half / this.h);
    gl.uniform1f(this.loc.u_time!, f.timeSec);
    gl.uniform1f(this.loc.u_presence!, f.presence);
    gl.uniform1f(this.loc.u_blink!, f.blink);
    gl.uniform1f(this.loc.u_gain!, f.gain);
    // Spacing is normalised against width; the sun is square so this is direct.
    gl.uniform1f(this.loc.u_pointSize!, Math.max(1, 0.0055 * 0.6 * 2 * f.size * this.dpr));
    const q = f.snap > 0 ? (f.snap * 2) / this.canvas.width : 0;
    gl.uniform2f(this.loc.u_snap!, q, q);

    gl.drawArrays(gl.POINTS, 0, b.count);
  }

  dispose(): void {
    const gl = this.gl;
    if (!gl) return;
    if (this.bufs) {
      gl.deleteBuffer(this.bufs.pos);
      gl.deleteBuffer(this.bufs.col);
      gl.deleteBuffer(this.bufs.seed);
      this.bufs = null;
    }
    if (this.prog) gl.deleteProgram(this.prog);
    this.prog = null;
  }
}

function compile(gl: WebGLRenderingContext, type: number, src: string): WebGLShader | null {
  const s = gl.createShader(type);
  if (!s) return null;
  gl.shaderSource(s, src);
  gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
    console.warn('[Sun] shader compile failed:', gl.getShaderInfoLog(s));
    gl.deleteShader(s);
    return null;
  }
  return s;
}
